/**
 * Per-campus payment choices for multi-campus families.
 *
 * Each unpaid campus can be settled as:
 *   - minimum: deposit required to re-enrol
 *   - full: the campus's full fees
 *
 * Family discounts stay family-wide. Early Bird uses the original full-payment
 * flow: the family must pay the full amount and cannot pick a campus minimum.
 */
import { getChildCampusName, groupChildrenByCampus } from './parentHistoryHelpers';

export const CAMPUS_PAYMENT_MINIMUM = 'minimum';
export const CAMPUS_PAYMENT_FULL = 'full';

export const DISCOUNT_HCC = 'Health Care Card Discount';
export const DISCOUNT_EBD = 'Early Bird Discount';
export const DISCOUNT_STAFF = 'Staff Discount';

const isYes = (value) => value === true || value === 'Yes' || value === 'yes';

export const campusPaymentKey = (campus = {}) =>
    String(campus.SessionID ?? campus.college ?? '').trim();

export const isMultiCampusPayment = (
    campusBreakdown = [],
    campusSessions = [],
    children = []
) => {
    if ((campusBreakdown || []).length > 1) return true;
    if ((campusSessions || []).length > 1) return true;
    return groupChildrenByCampus(children).length > 1;
};

/** Min vs full per campus is only for multi-campus families who did not pick EBD or staff. */
export const usesPerCampusPaymentOptions = ({
    isMultiCampus = false,
    staffDiscount = 'No',
    familyDiscountOption = '',
} = {}) => isMultiCampus && !isYes(staffDiscount) && familyDiscountOption !== DISCOUNT_EBD;

/**
 * Discount for a campus follows the family choice from the discounts page.
 * Paying in full does not grant Early Bird on its own.
 */
export const discountForCampusPayment = ({
    paymentMode,
    giveHccDiscount = false,
    staffDiscount = 'No',
    familyDiscountOption = '',
} = {}) => {
    if (isYes(staffDiscount) || familyDiscountOption === DISCOUNT_STAFF) return DISCOUNT_STAFF;
    if (giveHccDiscount || familyDiscountOption === DISCOUNT_HCC) return DISCOUNT_HCC;
    if (familyDiscountOption === DISCOUNT_EBD) return DISCOUNT_EBD;
    return '';
};

export const defaultCampusPaymentMode = ({
    existingMode,
    familyDiscountOption,
    isPaid = false,
} = {}) => {
    if (isPaid) return CAMPUS_PAYMENT_FULL;
    if (familyDiscountOption === DISCOUNT_EBD) return CAMPUS_PAYMENT_FULL;
    if (existingMode === CAMPUS_PAYMENT_MINIMUM || existingMode === CAMPUS_PAYMENT_FULL) {
        return existingMode;
    }
    return CAMPUS_PAYMENT_MINIMUM;
};

const selectionLookup = (selections = []) => {
    const byKey = new Map();
    (Array.isArray(selections) ? selections : []).forEach((row) => {
        if (!row) return;
        const keys = [
            campusPaymentKey(row),
            row.SessionID,
            row.college,
            String(row.college || '').toLowerCase(),
        ];
        keys.filter(Boolean).forEach((key) => byKey.set(String(key), row));
    });
    return byKey;
};

export const findCampusPaymentSelection = (selections, campus = {}) => {
    const byKey = selectionLookup(selections);
    return (
        byKey.get(campusPaymentKey(campus))
        || byKey.get(String(campus.SessionID || ''))
        || byKey.get(String(campus.college || ''))
        || byKey.get(String(campus.college || '').toLowerCase())
        || null
    );
};

export const buildDefaultCampusPaymentSelections = ({
    campusSessions = [],
    campusBreakdown = [],
    familyDiscountOption,
    giveHccDiscount = false,
    staffDiscount = 'No',
    existingSelections = [],
} = {}) => {
    const campuses = (campusBreakdown?.length ? campusBreakdown : campusSessions) || [];

    return campuses.map((campus) => {
        const existing = findCampusPaymentSelection(existingSelections, campus);
        const paymentMode = defaultCampusPaymentMode({
            existingMode: existing?.paymentMode || campus.paymentMode,
            familyDiscountOption,
            isPaid: campus.paymentStatus === 'paid',
        });

        return {
            SessionID: campus.SessionID,
            college: campus.college,
            paymentMode,
            DiscountOption:
                existing?.DiscountOption
                || campus.DiscountOption
                || discountForCampusPayment({
                    paymentMode,
                    giveHccDiscount,
                    staffDiscount,
                    familyDiscountOption,
                }),
        };
    }).filter((row) => campusPaymentKey(row));
};

export const campusPaymentSelectionsFromMap = (
    selectionsMap = {},
    campuses = [],
    discountContext = {}
) =>
    (campuses || []).map((campus) => {
        const key = campusPaymentKey(campus);
        // An entry is { paymentMode, amount }; a bare string is an older stored choice.
        const entry = selectionsMap[key];
        const stored = typeof entry === 'string' ? { paymentMode: entry } : (entry || {});
        const requestedMode = stored.paymentMode
            || campus.paymentMode
            || CAMPUS_PAYMENT_MINIMUM;
        const paymentMode = discountContext.familyDiscountOption === DISCOUNT_EBD
            ? CAMPUS_PAYMENT_FULL
            : requestedMode;
        return {
            SessionID: campus.SessionID,
            college: campus.college,
            paymentMode,
            // What the parent typed for this campus. The server holds it between that campus's
            // minimum and what it is owed.
            amount: stored.amount ?? null,
            DiscountOption: discountForCampusPayment({
                paymentMode,
                ...discountContext,
            }),
        };
    }).filter((row) => campusPaymentKey(row));

export const campusPaymentSelectionsToMap = (selections = []) => {
    const map = {};
    (Array.isArray(selections) ? selections : []).forEach((row) => {
        const key = campusPaymentKey(row);
        if (key) map[key] = { paymentMode: row.paymentMode, amount: row.amount ?? null };
    });
    return map;
};

export const amountsForCampusOptions = (campus = {}) => ({
    minimumAmount: Number(campus.minimumAmount ?? campus.TotalDepFees) || 0,
    fullAmount: Number(campus.fullAmount ?? campus.fullPayableAmount ?? campus.payableAmount) || 0,
});

export const campusPaymentOptionCopy = ({
    giveHccDiscount = false,
    familyDiscountOption = '',
} = {}) => ({
    [CAMPUS_PAYMENT_MINIMUM]: {
        title: 'Pay the minimum required for re-enrolment',
        description: giveHccDiscount
            ? 'Keep the 5% Health Care Card discount on school fees and pay only the deposit and resource fees required now. Remaining school fees can be paid later.'
            : 'Pay only the deposit and resource fees required to complete re-enrolment. Remaining school fees can be paid later.',
    },
    [CAMPUS_PAYMENT_FULL]: {
        title: 'Pay the full amount',
        description: giveHccDiscount
            ? 'Pay this campus’s full fees now with the 5% Health Care Card discount applied to school fees.'
            : familyDiscountOption === DISCOUNT_EBD
                ? 'Pay this campus’s full fees now, including resource fees, to receive the 5% Early Bird Discount on school fees.'
                : 'Pay this campus’s full fees now, including resource fees.',
    },
});

export const familyDiscountLabel = (campusBreakdown = [], fallback = '') => {
    const labels = [...new Set(
        (campusBreakdown || [])
            .map((row) => row.DiscountOption || row.selectedDiscount)
            .filter((label) => label && label !== 'No Discount')
    )];
    if (labels.length > 1) return 'Per-campus discounts';
    if (labels.length === 1) return labels[0];
    return fallback;
};

/**
 * How one campus's payment splits while the parent types, before the server answers.
 *
 * Credit belongs to this campus and is always spent first — a parent cannot keep credit back and
 * put the amount on a card instead. Whatever the credit does not cover goes on the card, and
 * anything left over stays at this campus.
 */
/**
 * Whether the family may choose how much to pay at each campus. A family at one campus chooses
 * for that campus exactly as a family at three does; Staff and Early Bird must be paid in full.
 */
export const allowsCampusPaymentAmounts = ({
    staffDiscount = 'No',
    familyDiscountOption = '',
} = {}) => !isYes(staffDiscount)
    && familyDiscountOption !== DISCOUNT_EBD
    && familyDiscountOption !== DISCOUNT_STAFF;

export const splitCampusPayment = ({ amount, creditAvailable } = {}) => {
    const paying = Math.max(0, Number(amount) || 0);
    const available = Math.max(0, Number(creditAvailable) || 0);
    const creditUsed = Math.min(available, paying);

    return {
        paying,
        creditUsed,
        cardAmount: Math.max(0, paying - creditUsed),
        creditRemaining: Math.max(0, available - creditUsed),
        coveredByCredit: paying > 0 && creditUsed >= paying,
    };
};

/** What this campus holds, whichever shape the server used. */
export const campusCreditAvailable = (campus = {}) =>
    Number(campus.credit_available ?? campus.creditAvailable ?? 0) || 0;

/**
 * Whether a campus is being paid in full, from what the server is charging for it: the amount it
 * will collect now against the full amount. That is the figure the card is charged, so the page
 * cannot say "in full" while the family pays the minimum, or the other way round. Null when the row
 * does not carry those figures (an older response), so the caller can fall back.
 */
export const campusPaidInFull = (row = {}) => {
    const full = Number(row.fullAmount);
    const charged = Number(row.amountToCover ?? row.CustomAmount);
    if (!Number.isFinite(full) || !Number.isFinite(charged) || full <= 0) return null;
    return charged >= full - 0.005;
};

/**
 * Whether the payment page should show the minimum re-enrolment fee.
 *
 * Shown whenever some campus still to pay is being charged less than its full amount — which is
 * exactly when the family is paying the minimum. Not when every campus still to pay is paid in full
 * (Early Bird, staff who chose to pay the year, or a campus set to full), and not once everything is
 * paid. Decided from the server's own figures where it sends them; the discount type alone cannot
 * say, because a staff family can choose not to pay in full and then pays the minimum.
 */
export const minimumFeeApplies = ({
    allowCampusAmounts = true,
    allCampusesPaid = false,
    campusBreakdown = [],
    selections = {},
} = {}) => {
    if (allCampusesPaid) return false;
    const toPay = (campusBreakdown || []).filter((row) => row.paymentStatus !== 'paid');
    const fromServer = toPay.map(campusPaidInFull);
    if (toPay.length && fromServer.every((value) => value !== null)) {
        return fromServer.some((inFull) => !inFull);
    }
    // An older response without the amounts: decided from the choices, as before.
    if (!allowCampusAmounts) return false;
    if (!toPay.length) return true;
    return !toPay.every((row) => (selections[campusPaymentKey(row)] || row.paymentMode) === CAMPUS_PAYMENT_FULL);
};

const sameText = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

/**
 * The campus breakdown row a child belongs to: by campus name, then by the campus's parent code.
 * Never by position — the children shown are filtered (Year 12 leavers drop out), so the order of
 * children and the order of campuses are not the same thing.
 */
export const campusRowForChild = (child = {}, campusBreakdown = []) => {
    const rows = campusBreakdown || [];
    const name = getChildCampusName(child);
    return (name && rows.find((row) => sameText(row.college, name)))
        || (child.parent_code && rows.find((row) => sameText(row.parent_code, child.parent_code)))
        || null;
};

/**
 * Whether one child's fee breakdown should show the minimum re-enrolment fee — the same rule as the
 * summary (minimumFeeApplies), applied to that child's campus alone. A family can pay one campus in
 * full and another the minimum, so this is decided per campus, never from the family's one answer.
 *
 * A child whose campus has no breakdown row gets the family's answer, so the line beside them can
 * never contradict the summary above it.
 */
export const minimumFeeAppliesForChild = ({
    child,
    allowCampusAmounts = true,
    allCampusesPaid = false,
    campusBreakdown = [],
    selections = {},
} = {}) => {
    const family = { allowCampusAmounts, allCampusesPaid, campusBreakdown, selections };
    if (allCampusesPaid) return false;
    const row = campusRowForChild(child, campusBreakdown);
    if (!row) return minimumFeeApplies(family);
    if (row.paymentStatus === 'paid') return false;
    return minimumFeeApplies({ ...family, campusBreakdown: [row] });
};
